/**
 * 🔥 GEOMETRY WORKER - Web Worker for heavy geometry processing
 * Runs in separate thread - doesn't block UI
 */

self.onmessage = async (event: MessageEvent) => {
  const { type, data, taskId, id } = event.data;

  // Heartbeat: respond to ping immediately
  if (type === 'ping') {
    self.postMessage({ type: 'pong', id });
    return;
  }

  try {
    let result: any;

    switch (type) {
      case 'parse-stl':
        result = parseSTL(data.buffer);
        break;
      case 'parse-dxf':
        result = parseDXF(data.content);
        break;
      case 'optimize-geometry':
        result = optimizeGeometry(data.vertices, data.indices);
        break;
      case 'compress-vertices':
        result = compressVertices(data.vertices);
        break;
      case 'compute-normals':
        result = computeNormals(data.vertices, data.indices);
        break;
      default:
        throw new Error(`Unknown task type: ${type}`);
    }

    // Collect transferable ArrayBuffers for zero-copy transfer
    const transferables: ArrayBuffer[] = [];
    if (result) {
      if (result.vertices?.buffer) transferables.push(result.vertices.buffer);
      if (result.indices?.buffer) transferables.push(result.indices.buffer);
      if (result.normals?.buffer) transferables.push(result.normals.buffer);
      if (result instanceof Uint32Array || result instanceof Float32Array || result instanceof Uint8Array) {
        transferables.push(result.buffer);
      }
    }
    self.postMessage({ taskId, result }, transferables as any);
  } catch (error) {
    self.postMessage({ taskId, error: (error as Error).message });
  }
};

// ============================================================================
// STL PARSING - ULTRA-OPTIMIZED FOR WORKER THREAD
// ============================================================================

interface STLResult {
  vertices: Float32Array;
  indices: Uint32Array;
  normals: Float32Array;
}

function parseSTL(buffer: ArrayBuffer): STLResult {
  const uint8Array = new Uint8Array(buffer);
  const view = new DataView(buffer);

  // Detect binary vs ASCII
  const isBinary = !new TextDecoder('utf-8', { fatal: false })
    .decode(uint8Array.slice(0, 5))
    .toLowerCase()
    .startsWith('solid');

  if (isBinary) {
    return parseSTLBinary(view, uint8Array);
  } else {
    return parseSTLASCII(uint8Array);
  }
}

function parseSTLBinary(view: DataView, uint8Array: Uint8Array): STLResult {
  if (uint8Array.length < 84) {
    throw new Error('Invalid binary STL: buffer too small');
  }

  const triangleCount = view.getUint32(80, true);
  const expectedSize = 84 + triangleCount * 50;

  if (uint8Array.length < expectedSize) {
    throw new Error('Truncated binary STL file');
  }

  const vertices: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];

  let offset = 84;
  for (let i = 0; i < triangleCount; i++) {
    // Read normal
    const nx = view.getFloat32(offset, true);
    const ny = view.getFloat32(offset + 4, true);
    const nz = view.getFloat32(offset + 8, true);
    offset += 12;

    // Read 3 vertices
    for (let j = 0; j < 3; j++) {
      vertices.push(
        view.getFloat32(offset, true),
        view.getFloat32(offset + 4, true),
        view.getFloat32(offset + 8, true)
      );
      offset += 12;
    }

    // Add indices
    const idx = i * 3;
    indices.push(idx, idx + 1, idx + 2);

    // Add normals (same for all vertices in triangle)
    for (let j = 0; j < 3; j++) {
      normals.push(nx, ny, nz);
    }

    // Skip attribute byte count
    offset += 2;
  }

  return {
    vertices: new Float32Array(vertices),
    indices: new Uint32Array(indices),
    normals: new Float32Array(normals)
  };
}

function parseSTLASCII(uint8Array: Uint8Array): STLResult {
  const text = new TextDecoder().decode(uint8Array);
  const vertices: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];

  const lines = text.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line.startsWith('facet normal')) {
      const parts = line.split(/\s+/);
      const nx = parseFloat(parts[2] || '0');
      const ny = parseFloat(parts[3] || '0');
      const nz = parseFloat(parts[4] || '0');

      i++;
      let vertexCount = 0;

      while (i < lines.length) {
        const vline = lines[i].trim();

        if (vline.startsWith('vertex')) {
          const vparts = vline.split(/\s+/);
          vertices.push(
            parseFloat(vparts[1] || '0'),
            parseFloat(vparts[2] || '0'),
            parseFloat(vparts[3] || '0')
          );
          vertexCount++;
        } else if (vline.startsWith('endfacet')) {
          break;
        }

        i++;
      }

      if (vertexCount === 3) {
        const idx = vertices.length / 3 - 3;
        indices.push(idx, idx + 1, idx + 2);
        for (let j = 0; j < 3; j++) {
          normals.push(nx, ny, nz);
        }
      }
    }

    i++;
  }

  return {
    vertices: new Float32Array(vertices),
    indices: new Uint32Array(indices),
    normals: new Float32Array(normals)
  };
}

// ============================================================================
// DXF PARSING - LIGHTWEIGHT
// ============================================================================

function parseDXF(content: string): any {
  const entities: any[] = [];
  const lines = content.split('\n');
  let inEntities = false;

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed === 'ENTITIES') {
      inEntities = true;
    } else if (inEntities && trimmed === 'ENDSEC') {
      break;
    } else if (inEntities && trimmed.match(/LINE|CIRCLE|ARC|POLYLINE/)) {
      entities.push({ type: trimmed });
    }
  }

  return { entities, entityCount: entities.length };
}

// ============================================================================
// GEOMETRY OPTIMIZATION
// ============================================================================

/**
 * Optimize vertex cache for better GPU performance
 * Reorders indices using Forsyth algorithm
 */
function optimizeGeometry(vertices: Float32Array, indices: Uint32Array): Uint32Array {
  const triangleCount = indices.length / 3;
  const cacheSize = 32;
  const result = new Uint32Array(indices.length);
  const processed = new Uint8Array(triangleCount);
  const lastVertexTime = new Float32Array(vertices.length / 3).fill(-1);

  let outputIndex = 0;
  let time = 0;

  for (let i = 0; i < triangleCount; i++) {
    let bestScore = -Infinity;
    let bestTriangle = -1;

    for (let j = 0; j < triangleCount; j++) {
      if (processed[j]) continue;

      // Score based on vertex cache hits
      let score = 0;
      for (let k = 0; k < 3; k++) {
        const vertex = indices[j * 3 + k];
        const lastTime = lastVertexTime[vertex];
        if (lastTime >= 0) {
          const timeSinceUsed = time - lastTime;
          if (timeSinceUsed < cacheSize) {
            score += (cacheSize - timeSinceUsed) / cacheSize;
          }
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestTriangle = j;
      }
    }

    if (bestTriangle >= 0) {
      processed[bestTriangle] = 1;
      for (let k = 0; k < 3; k++) {
        const vertex = indices[bestTriangle * 3 + k];
        result[outputIndex++] = vertex;
        lastVertexTime[vertex] = time++;
      }
    }
  }

  return result;
}

// ============================================================================
// VERTEX COMPRESSION
// ============================================================================

/**
 * Compress vertices using uint16 quantization
 * Reduces from float32 (4 bytes) to uint16 (2 bytes) = 50% reduction
 */
function compressVertices(vertices: Float32Array): Uint8Array {
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;

  // Find bounds
  for (let i = 0; i < vertices.length; i += 3) {
    const x = vertices[i];
    const y = vertices[i + 1];
    const z = vertices[i + 2];

    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }

  // Quantize
  const result = new Uint8Array(vertices.length / 3 * 6);
  const view = new Uint16Array(result.buffer);

  for (let i = 0; i < vertices.length; i += 3) {
    const x = Math.round(((vertices[i] - minX) / (maxX - minX)) * 65535);
    const y = Math.round(((vertices[i + 1] - minY) / (maxY - minY)) * 65535);
    const z = Math.round(((vertices[i + 2] - minZ) / (maxZ - minZ)) * 65535);

    view[(i / 3) * 3] = x;
    view[(i / 3) * 3 + 1] = y;
    view[(i / 3) * 3 + 2] = z;
  }

  return result;
}

// ============================================================================
// NORMAL COMPUTATION
// ============================================================================

/**
 * Compute smooth vertex normals from geometry
 */
function computeNormals(vertices: Float32Array, indices: Uint32Array): Float32Array {
  const normals = new Float32Array(vertices.length);

  // Accumulate face normals
  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i] * 3;
    const i1 = indices[i + 1] * 3;
    const i2 = indices[i + 2] * 3;

    // Vectors from v0 to v1 and v0 to v2
    const e1x = vertices[i1] - vertices[i0];
    const e1y = vertices[i1 + 1] - vertices[i0 + 1];
    const e1z = vertices[i1 + 2] - vertices[i0 + 2];

    const e2x = vertices[i2] - vertices[i0];
    const e2y = vertices[i2 + 1] - vertices[i0 + 1];
    const e2z = vertices[i2 + 2] - vertices[i0 + 2];

    // Face normal = e1 × e2
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;

    // Normalize
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) {
      const invLen = 1 / len;
      const nnx = nx * invLen;
      const nny = ny * invLen;
      const nnz = nz * invLen;

      // Add to all vertices
      normals[i0] += nnx;
      normals[i0 + 1] += nny;
      normals[i0 + 2] += nnz;

      normals[i1] += nnx;
      normals[i1 + 1] += nny;
      normals[i1 + 2] += nnz;

      normals[i2] += nnx;
      normals[i2 + 1] += nny;
      normals[i2 + 2] += nnz;
    }
  }

  // Normalize all vertices
  for (let i = 0; i < normals.length; i += 3) {
    const nx = normals[i];
    const ny = normals[i + 1];
    const nz = normals[i + 2];

    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) {
      const invLen = 1 / len;
      normals[i] *= invLen;
      normals[i + 1] *= invLen;
      normals[i + 2] *= invLen;
    }
  }

  return normals;
}
